import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  FormField,
  Modal,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import {
  ContentAccessUser,
  ContentWorkflow,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import { useState } from 'react';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { createWorkflowEventWithStatePolling } from '../../api/CreateWorkflowEventWithStatePolling';
import BrowserHistory from '../utils/BrowserHistory';
import { DRAFT_STATES } from '../../api/ContentUtil';
import { waitForContentAvailablity } from '../../api/UtilsApi';
import constants from '../../common/constants';
import { getContentPath } from '../../common/ContentPaths';
import UserIdAutosuggest from '../common/UserIdAutosuggest';

interface AuthorActionsDropdownProps {
  readonly contentId: string;
  readonly ownerId: string;
  readonly workflow: ContentWorkflow;
  readonly isAuthor: boolean;
  readonly isAuthorOrPrivileged?: boolean;
  readonly onComplete?: () => void;
}

const mplService: IMplServiceClient = MplServiceClientFactory.create();

const AuthorActionsDropdown = ({
  contentId,
  ownerId,
  workflow,
  isAuthor,
  isAuthorOrPrivileged = true,
  onComplete,
}: AuthorActionsDropdownProps) => {
  const { draftWorkflowId } = workflow;
  const [showCoAuthorModal, setShowCoAuthorModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAcceptingUpdate, setIsAcceptingUpdate] = useState(false);
  const [acceptUpdateError, setAcceptUpdateError] = useState<string | null>(null);

  // CoAuthor modal state
  const [sharedWithUsers, setSharedWithUsers] = useState<ContentAccessUser[]>([]);
  const [reviewers, setReviewers] = useState<ContentAccessUser[]>([]);
  const [coAuthorId, setCoAuthorId] = useState<string>();
  const [coAuthorName, setCoAuthorName] = useState<string>();
  const [coAuthorValid, setCoAuthorValid] = useState<boolean>(false);
  const [coAuthorError, setCoAuthorError] = useState<string>();

  const acceptUpdate = async () => {
    setIsAcceptingUpdate(true);
    setAcceptUpdateError(null);
    try {
      if (
        !workflow?.isLocked &&
        DRAFT_STATES.includes(workflow?.draftState) &&
        workflow?.publicationState !== WorkflowState.FINALIZING
      ) {
        await createWorkflowEventWithStatePolling(
          contentId,
          draftWorkflowId,
          WorkflowEventTypes.LOCK,
          '',
        );
      }
      if (!isAuthor) {
        await waitForContentAvailablity(contentId);
      }
      BrowserHistory.push(getContentPath({ contentId, mode: 'draft', role: 'author' }));
    } catch (error) {
      console.error('Failed to accept update:', error);
      setAcceptUpdateError('Unable to process your request. Please try again later.');
    } finally {
      setIsAcceptingUpdate(false);
    }
  };

  // CoAuthor helpers
  const checkContentAlreadyShared = (selectedUserId: string) => {
    if (ownerId === selectedUserId) {
      return `User is already the content owner. Select a different user.`;
    }
    if (sharedWithUsers.some((us) => us.userId === selectedUserId)) {
      return `Content already shared with user. Select a different user.`;
    }
    if (reviewers.some((rv) => rv.userId === selectedUserId)) {
      return `User is already content's reviewer. Select a different user.`;
    }
    return null;
  };

  const handleCoauthorChange = (
    selectedUserId: string,
    selectedUserName: string,
    validationError: string,
  ) => {
    setCoAuthorId(selectedUserId);
    setCoAuthorName(selectedUserName);

    if (!selectedUserId || validationError) {
      setCoAuthorValid(false);
      setCoAuthorError(validationError);
      return;
    }

    const alreadySharedError = checkContentAlreadyShared(selectedUserId);
    if (alreadySharedError) {
      setCoAuthorError(alreadySharedError);
      setCoAuthorValid(false);
    } else {
      setCoAuthorError(undefined);
      setCoAuthorValid(true);
    }
  };

  const openCoAuthorModal = async () => {
    try {
      const [{ accessUsers: coAuthors }, { accessUsers: peerReviewers }] = await Promise.all([
        mplService.listContentSharedWith(contentId),
        mplService.listContentReviewers(contentId),
      ]);
      setSharedWithUsers(coAuthors);
      setReviewers(peerReviewers);
      setShowCoAuthorModal(true);
    } catch (error) {
      console.error('Failed to load co-author data:', error);
    }
  };

  const addCoauthor = async () => {
    if (!coAuthorId) return;
    setIsSubmitting(true);
    setCoAuthorError(undefined);
    try {
      await mplService.shareContents([contentId], [coAuthorId]);
      setShowCoAuthorModal(false);
      onComplete?.();
    } catch (error) {
      console.error('Failed to add co-author:', error);
      setCoAuthorError('Unable to add co-author. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleItemClick = (event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
    switch (event.detail.id) {
      case 'make-updates':
        acceptUpdate();
        break;
      case 'get-help':
        openCoAuthorModal();
        break;
    }
  };

  const resetCoAuthorModal = () => {
    setShowCoAuthorModal(false);
    setCoAuthorId(undefined);
    setCoAuthorName(undefined);
    setCoAuthorValid(false);
    setCoAuthorError(undefined);
  };

  const items: ButtonDropdownProps.Items = isAuthorOrPrivileged
    ? [
        {
          id: 'make-updates',
          text: constants.CONTENT_UPDATES_ACTION_UPDATE_BUTTON_TEXT,
        },
        {
          id: 'get-help',
          text: constants.CONTENT_UPDATES_ACTION_ADD_COAUTHOR,
        },
      ]
    : [
        {
          id: 'get-help',
          text: 'Become co-author',
        },
      ];

  return (
    <>
      <SpaceBetween size="s">
        {acceptUpdateError && (
          <Alert type="error" dismissible onDismiss={() => setAcceptUpdateError(null)}>
            {acceptUpdateError}
          </Alert>
        )}
        <ButtonDropdown items={items} onItemClick={handleItemClick} loading={isAcceptingUpdate}>
          Actions
        </ButtonDropdown>
      </SpaceBetween>

      {/* CoAuthor Modal */}
      {showCoAuthorModal && (
        <Modal
          visible={showCoAuthorModal}
          header={constants.CONTENT_UPDATES_ACTION_COAUTHOR_HEADER}
          onDismiss={resetCoAuthorModal}
          closeAriaLabel="Close modal"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={resetCoAuthorModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={addCoauthor}
                  loading={isSubmitting}
                  disabled={!coAuthorValid}
                >
                  {constants.CONTENT_UPDATES_ACTION_COAUTHOR_BUTTON}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <FormField>{constants.CONTENT_UPDATES_ACTION_COAUTHOR_MESSAGE}</FormField>
            {coAuthorError && <Alert type="warning">{coAuthorError}</Alert>}
            <FormField label={constants.CONTENT_UPDATES_ACTION_COAUTHOR_LABEL}>
              <UserIdAutosuggest
                userId={coAuthorId}
                userName={coAuthorName}
                isRequired={false}
                onUserSelected={handleCoauthorChange}
              />
            </FormField>
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};

export default AuthorActionsDropdown;
